import { useEffect, useState } from "react";
import MediaUploader from "../../components/Dashboard/MediaUploader";
import Loader from "../../components/UI/Loader";
import { deleteUpload, getMediaAssets, uploadFiles } from "../../services/uploadService";

const INITIAL_CATEGORIES = [
  "Produk IoT",
  "Sensor",
  "Module",
  "Starter Kit",
  "Banner Promo",
  "Dokumentasi",
  "Video Demo", // Tambahan default untuk video
  "Lainnya"
];

// Helper: Deteksi tipe file
const isVideo = (url) => {
  if (!url) return false;
  return url.match(/\.(mp4|webm|ogg|mov)$/i);
};

const MediaPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // State Kategori Upload
  const [categories, setCategories] = useState(INITIAL_CATEGORIES);
  const [selectedClass, setSelectedClass] = useState(INITIAL_CATEGORIES[0]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  // ==========================================
  // STATE ADVANCED GALLERY (FITUR BARU)
  // ==========================================
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("Semua");
  const [sortOrder, setSortOrder] = useState("newest"); // newest, oldest, az, za
  const [viewMode, setViewMode] = useState("grid"); // grid atau list

  const fetchMedia = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getMediaAssets();
      const fetchedFiles = Array.isArray(data) ? data : data?.results || [];
      setFiles(fetchedFiles);

      const existingCategories = [...new Set(fetchedFiles.map(file => file.category || file.class).filter(Boolean))];
      const mergedCategories = [...new Set([...INITIAL_CATEGORIES, ...existingCategories])];
      setCategories(mergedCategories);
    } catch (err) {
      setError(err.message || "Gagal memuat media.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const handleUpload = async (selectedFiles) => {
    if (!selectedFiles?.length) return;
    setUploading(true);
    setError("");
    setSuccessMessage("");
    try {
      const uploaded = await uploadFiles(selectedFiles, selectedClass);
      setFiles((prev) => [...uploaded, ...prev]);
      setSuccessMessage(`Media berhasil diupload ke kategori: ${selectedClass}`);
      
      // Reset kategori upload jika membuat yang baru
      if (!categories.includes(selectedClass)) {
        setCategories(prev => [...prev, selectedClass]);
      }
    } catch (err) {
      setError(err.message || "Gagal upload media.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (file) => {
    if (!window.confirm(`Hapus media "${file.name}" secara permanen?`)) return;
    try {
      await deleteUpload(file.id);
      setFiles((prev) => prev.filter((item) => item.id !== file.id));
      setSuccessMessage("Media berhasil dihapus.");
    } catch (err) {
      setError(err.message || "Gagal menghapus media.");
    }
  };

  const handleSaveNewCategory = () => {
    if (newCategory.trim() === "") return;
    if (!categories.includes(newCategory.trim())) {
      setCategories((prev) => [...prev, newCategory.trim()]);
    }
    setSelectedClass(newCategory.trim());
    setIsAddingNew(false);
    setNewCategory("");
  };

  // ==========================================
  // LOGIKA FILTERING, SEARCHING & SORTING
  // ==========================================
  const processedFiles = files
    .filter(file => {
      // 1. Filter Kategori
      const fileCat = file.category || file.class || "Lainnya";
      const matchCategory = filterCategory === "Semua" || fileCat === filterCategory;
      
      // 2. Filter Pencarian Nama
      const matchSearch = file.name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchCategory && matchSearch;
    })
    .sort((a, b) => {
      // 3. Pengurutan (Sorting)
      if (sortOrder === "newest") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortOrder === "oldest") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortOrder === "az") return (a.name || "").localeCompare(b.name || "");
      if (sortOrder === "za") return (b.name || "").localeCompare(a.name || "");
      return 0;
    });

  // Ekstrak kategori unik khusus untuk dropdown filter
  const filterCategoriesList = ["Semua", ...new Set(files.map(f => f.category || f.class).filter(Boolean))];

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", fontFamily: "'Nunito', sans-serif" }}>
      
      <style>
        {`
          .adv-media-card { background: #fff; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #fce7f3; margin-bottom: 24px; overflow: hidden; }
          .adv-media-header { background: #fffafc; padding: 16px 20px; border-bottom: 1px solid #fce7f3; display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; align-items: center; }
          .adv-media-title { font-size: 1.1rem; font-weight: 800; color: #8e1628; margin: 0; }
          
          /* Toolbar Elements */
          .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; padding: 16px 20px; background: #ffffff; align-items: center; }
          .adv-input, .adv-select { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none; transition: border 0.2s; }
          .adv-input:focus, .adv-select:focus { border-color: #ec4899; box-shadow: 0 0 0 2px rgba(236,72,153,0.1); }
          .adv-search { flex-grow: 1; min-width: 200px; }
          
          /* View Toggles */
          .adv-view-toggles { display: flex; gap: 4px; background: #f3f4f6; padding: 4px; border-radius: 8px; }
          .adv-view-btn { padding: 6px 12px; border: none; background: transparent; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 700; color: #6b7280; transition: all 0.2s; }
          .adv-view-btn.active { background: #ffffff; color: #8e1628; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

          /* GRID VIEW */
          .adv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 16px; padding: 20px; }
          .adv-grid-item { position: relative; aspect-ratio: 1; border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; background: #f9fafb; group; cursor: pointer; }
          .adv-grid-media { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
          .adv-grid-item:hover .adv-grid-media { transform: scale(1.05); }
          .adv-grid-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.6); opacity: 0; transition: opacity 0.2s; display: flex; flex-direction: column; justify-content: space-between; padding: 12px; }
          .adv-grid-item:hover .adv-grid-overlay { opacity: 1; }
          
          /* LIST VIEW */
          .adv-list { display: flex; flex-direction: column; padding: 0; margin: 0; }
          .adv-list-item { display: flex; align-items: center; gap: 16px; padding: 12px 20px; border-bottom: 1px solid #f3f4f6; transition: background 0.2s; }
          .adv-list-item:hover { background: #fffafc; }
          .adv-list-thumb { width: 60px; height: 60px; border-radius: 8px; overflow: hidden; flex-shrink: 0; border: 1px solid #e5e7eb; position: relative; }
          .adv-list-details { flex-grow: 1; overflow: hidden; }
          .adv-list-title { font-weight: 700; color: #1f2937; margin: 0 0 4px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .adv-list-meta { font-size: 12px; color: #6b7280; display: flex; gap: 12px; }
          
          /* Badge Kategori */
          .adv-badge { font-size: 11px; padding: 2px 8px; border-radius: 12px; background: #fce7f3; color: #be185d; font-weight: 700; white-space: nowrap; }
          .adv-badge.video { background: #e0e7ff; color: #3730a3; }
          
          /* Delete Action */
          .adv-delete-btn { background: #fee2e2; color: #ef4444; border: none; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; transition: 0.2s; }
          .adv-delete-btn:hover { background: #fecaca; }

          /* Play Icon Overlay for Videos */
          .play-icon-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.5); border-radius: 50%; width: 40px; height: 40px; display: flex; justify-content: center; align-items: center; pointer-events: none; }
          .play-icon-overlay svg { fill: white; width: 20px; height: 20px; margin-left: 3px; }
        `}
      </style>

      <div className="admin-page-header" style={{ marginBottom: "24px" }}>
        <h2 className="admin-page-title" style={{ fontSize: "28px", color: "#8e1628" }}>Media Library BukuOiBook</h2>
        <p className="admin-page-desc">Kelola semua aset foto buku dan video promosi Anda di sini.</p>
      </div>

      {loading && <Loader text="Memuat media..." />}
      {uploading && <Loader text="Mengupload media..." />}
      {error && <div className="admin-info-box" style={{ marginBottom: "16px", color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca" }}>{error}</div>}
      {successMessage && <div className="admin-info-box" style={{ marginBottom: "16px", color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0" }}>{successMessage}</div>}

      {/* ===================================== */}
      {/* 1. KOTAK UPLOAD FILE */}
      {/* ===================================== */}
      <div className="adv-media-card">
        <div className="adv-media-header">
          <h3 className="adv-media-title">Upload Media Baru</h3>
          
          {/* Pemilihan Kategori Upload */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span style={{ fontSize: "13px", fontWeight: "bold", color: "#4b5563" }}>Kategori:</span>
            {!isAddingNew ? (
              <select className="adv-select" value={selectedClass} onChange={(e) => { if (e.target.value === "TAMBAH_BARU") setIsAddingNew(true); else setSelectedClass(e.target.value); }}>
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                <option value="TAMBAH_BARU" style={{ fontWeight: "bold", color: "#ec4899" }}>+ Kategori Baru...</option>
              </select>
            ) : (
              <div style={{ display: "flex", gap: "6px" }}>
                <input type="text" className="adv-input" placeholder="Nama Kategori..." value={newCategory} onChange={(e) => setNewCategory(e.target.value)} autoFocus />
                <button onClick={handleSaveNewCategory} className="adv-view-btn active" style={{ background: "#16a34a", color: "white" }}>OK</button>
                <button onClick={() => setIsAddingNew(false)} className="adv-view-btn active" style={{ background: "#ef4444", color: "white" }}>Batal</button>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ padding: "0 20px 20px 20px" }}>
          {/* Menggunakan MediaUploader hanya sebagai Dropzone (mengosongkan daftar file) */}
          <MediaUploader files={[]} onUpload={handleUpload} onRemove={() => {}} />
        </div>
      </div>


      {/* ===================================== */}
      {/* 2. ADVANCED GALLERY AREA */}
      {/* ===================================== */}
      <div className="adv-media-card">
        
        {/* TOOLBAR */}
        <div className="adv-toolbar" style={{ borderBottom: "1px solid #e5e7eb" }}>
          
          {/* Search Box */}
          <input 
            type="text" 
            className="adv-input adv-search" 
            placeholder="Cari nama file..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Filter Category */}
          <select className="adv-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            {filterCategoriesList.map(cat => <option key={cat} value={cat}>{cat === "Semua" ? "Semua Kategori" : cat}</option>)}
          </select>

          {/* Sort Order */}
          <select className="adv-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="newest">Terbaru</option>
            <option value="oldest">Terlama</option>
            <option value="az">Nama A-Z</option>
            <option value="za">Nama Z-A</option>
          </select>

          {/* View Toggles (Detail List vs Small Grid) */}
          <div className="adv-view-toggles" style={{ marginLeft: "auto" }}>
            <button className={`adv-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
              Grid (Kecil)
            </button>
            <button className={`adv-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
              Detail (List)
            </button>
          </div>
        </div>

        {/* HASIL FILTER */}
        <div style={{ padding: "8px 20px", background: "#f9fafb", fontSize: "13px", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
          Menampilkan {processedFiles.length} file.
        </div>

        {/* OUTPUT: GRID VIEW */}
        {viewMode === "grid" && (
          <div className="adv-grid">
            {processedFiles.map(file => {
              const url = file.file_url || file.url || file.file;
              const isVid = isVideo(url);
              const cat = file.category || file.class || "Lainnya";

              return (
                <div key={file.id} className="adv-grid-item" title={file.name}>
                  {isVid ? (
                    <>
                      <video src={url} className="adv-grid-media" muted preload="metadata" />
                      <div className="play-icon-overlay"><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>
                    </>
                  ) : (
                    <img src={url} alt={file.name} className="adv-grid-media" loading="lazy" />
                  )}
                  
                  {/* Overlay Info on Hover */}
                  <div className="adv-grid-overlay">
                    <div style={{ alignSelf: "flex-end" }}>
                      <span className={`adv-badge ${isVid ? 'video' : ''}`}>{cat}</span>
                    </div>
                    <div>
                      <p style={{ color: "white", fontSize: "12px", margin: "0 0 8px 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</p>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={() => window.open(url, '_blank')} style={{ flex: 1, padding: "4px", background: "#fff", border: "none", borderRadius: "4px", fontSize: "11px", cursor: "pointer", fontWeight: "bold" }}>Lihat</button>
                        <button onClick={() => handleRemove(file)} style={{ padding: "4px 8px", background: "#ef4444", color: "white", border: "none", borderRadius: "4px", fontSize: "11px", cursor: "pointer" }}>Hapus</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* OUTPUT: LIST/DETAIL VIEW */}
        {viewMode === "list" && (
          <div className="adv-list">
            {processedFiles.map(file => {
              const url = file.file_url || file.url || file.file;
              const isVid = isVideo(url);
              const cat = file.category || file.class || "Lainnya";
              const dateObj = file.created_at ? new Date(file.created_at) : null;
              const dateStr = dateObj ? `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}` : '-';

              return (
                <div key={file.id} className="adv-list-item">
                  
                  <div className="adv-list-thumb">
                    {isVid ? (
                      <>
                        <video src={url} className="adv-grid-media" muted preload="metadata" />
                        <div className="play-icon-overlay" style={{ width: "24px", height: "24px" }}><svg viewBox="0 0 24 24" style={{ width:"12px", height:"12px" }}><path d="M8 5v14l11-7z"/></svg></div>
                      </>
                    ) : (
                      <img src={url} alt={file.name} className="adv-grid-media" loading="lazy" />
                    )}
                  </div>
                  
                  <div className="adv-list-details">
                    <h4 className="adv-list-title" title={file.name}>{file.name}</h4>
                    <div className="adv-list-meta">
                      <span className={`adv-badge ${isVid ? 'video' : ''}`}>{cat}</span>
                      <span>{isVid ? "Video" : "Gambar"}</span>
                      <span>Diunggah: {dateStr}</span>
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#ec4899", textDecoration: "none" }}>Buka Link ↗</a>
                    </div>
                  </div>

                  <button className="adv-delete-btn" onClick={() => handleRemove(file)}>
                    Hapus
                  </button>

                </div>
              );
            })}
          </div>
        )}

        {/* EMPTY STATE */}
        {processedFiles.length === 0 && !loading && (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
            <span style={{ fontSize: "48px", display: "block", marginBottom: "16px" }}>📭</span>
            <p style={{ fontSize: "16px", fontWeight: "600", margin: 0 }}>Tidak ada file yang ditemukan.</p>
            <p style={{ fontSize: "14px" }}>Coba ubah kata kunci pencarian atau filter kategori Anda.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MediaPage;